// Skripte und Drehbücher: Datenform, Vorlagen und Laufzeit (Browser und Server).
// Ein Drehbuch hat ein kurzes Briefing, den Drehtag und ein oder mehrere Videos.
// Ein Video ist eine Abfolge von Zeilen: Zeit | Bild (was man sieht) | Ton (was man hört).
// Die Art ergibt sich aus dem Ton: «Name: Text» ist ein Statement, «Sprecher: Text» die Off-Stimme,
// alles andere (z. B. [Musik]) gehört zum Bild. Ein Ort gilt, bis ein neuer kommt.

export type ScriptType = "social" | "image" | "interview" | "event";
export type ScriptStatus = "draft" | "review" | "approved";

export type BeatKind = "bild" | "statement" | "sprecher";
/** Ein Moment im Video, eine Zeile im Drehbuch */
export interface ScriptBeat {
  id: string;
  kind: BeatKind;
  /** Name des Moments auf der Zeitachse (optional), z. B. «Hook» oder «Schluss» */
  label: string;
  /** Statement: wer spricht (Rolle oder Name) */
  who: string;
  /** Bild: was man sieht */
  visual: string;
  /** Ton: Aussage, Sprechertext oder Musik/Atmo */
  audio: string;
  /** Einstellungsgrösse */
  shot: string;
  location: string;
  /** Sekunden (leer = aus dem Text geschätzt) */
  duration: string;
  /** Text im Bild: Bauchbinde, Texttafel, Logo */
  overlay: string;
  /** Alternativen zum Text, aus denen am Set gewählt wird */
  options: string[];
  /** Regie-Notiz */
  note: string;
}
export interface ScriptQuestion {
  id: string;
  text: string;
  note: string;
}
export interface ScriptField {
  id: string;
  /** feste Bedeutung (z. B. «shootDate»); leer bei eigenen Angaben */
  key: string;
  label: string;
  value: string;
  /** mehrzeilig */
  long: boolean;
}
export interface ScriptVideo {
  id: string;
  title: string;
  format: string;
  targetLength: string;
  /** Idee in einem Satz */
  idea: string;
  beats: ScriptBeat[];
  questions: ScriptQuestion[];
}
export interface ScriptContent {
  v: 4;
  /** Briefing: gilt für alle Videos */
  brief: ScriptField[];
  /** Drehtag */
  production: ScriptField[];
  videos: ScriptVideo[];
}
export interface ScriptMeta {
  id: string;
  title: string;
  type: ScriptType;
  status: ScriptStatus;
  version: number;
  clientId: string | null;
  projectId: string | null;
  author: string;
  createdAt: string;
  updatedAt: string;
}
export interface ScriptListItem extends ScriptMeta {
  seconds: number;
  shots: number;
  targetLength: string;
  videos: number;
}
export interface ScriptFull extends ScriptMeta {
  content: ScriptContent;
}
export interface ScriptVersionRow {
  id: string;
  version: number;
  note: string;
  createdAt: string;
}

export const SCRIPT_TYPES: Record<ScriptType, { label: string; desc: string; format: string; length: string }> = {
  social: { label: "Social-Media-Clip", desc: "Reel / TikTok, Hook in den ersten Sekunden, Einblendungen, klarer Call-to-Action", format: "9:16", length: "30 s" },
  image: { label: "Imagefilm", desc: "Firmen- oder Produktvideo mit Sprecher und Bildfolge", format: "16:9", length: "1:30" },
  interview: { label: "Interview / Testimonial", desc: "Fragen, Statements und B-Roll", format: "16:9", length: "1:00" },
  event: { label: "Event / Rückblick", desc: "Impressionen, Stimmen und Stimmung", format: "16:9", length: "45 s" },
};
export const SCRIPT_STATUS: Record<ScriptStatus, string> = { draft: "Entwurf", review: "Beim Kunden", approved: "Freigegeben" };
export const BEAT_KINDS: Record<BeatKind, { label: string; ph: string; visual: string }> = {
  bild: { label: "Bild", ph: "Musik, Atmo, Originalton …", visual: "Was sieht man?" },
  statement: { label: "Statement", ph: "Was die Person sagt", visual: "Bild zum Statement, z. B. vor der Maschine" },
  sprecher: { label: "Sprecher", ph: "Text der Off-Stimme", visual: "Was sieht man dazu?" },
};
/** «Name: Text» erkennen. Sprecher/Off/Voice-over ist die Off-Stimme. */
const VO = /^(sprecher(in)?|off|off-stimme|voice[- ]?over|vo)$/i;
export function parseSound(audio: string): { kind: BeatKind; who: string; text: string } {
  const t = audio.trim();
  const m = !/^\S+:\/\//.test(t) && t.match(/^([^:\n«»"[\]]{2,40}):\s*([\s\S]*)$/);
  if (m) {
    const who = m[1]!.trim();
    const text = m[2]!.trim().replace(/^[«"]\s*/, "").replace(/\s*[»"]$/, "");
    return VO.test(who) ? { kind: "sprecher", who: "Sprecher", text } : { kind: "statement", who, text };
  }
  return { kind: "bild", who: "", text: t };
}
/** Art und Sprecher/in aus dem Ton übernehmen */
export function syncBeat(b: ScriptBeat) {
  const p = parseSound(b.audio);
  b.kind = p.kind;
  b.who = p.who;
  return b;
}
/** Ort jeder Zeile: gilt, bis ein neuer kommt */
export const placesOf = (v: ScriptVideo) => {
  let cur = "";
  return v.beats.map((b) => (cur = b.location.trim() || cur));
};
/** Bild ohne Angabe: übliche Länge einer Einstellung */
export const DEFAULT_SHOT_SECONDS = 3;
export const SHOT_SIZES = ["Totale", "Halbtotale", "Halbnah", "Nah", "Gross", "Detail", "POV", "Drohne", "Gimbal / Fahrt", "Zeitlupe", "Screen / Grafik"];
export const FORMATS = ["16:9", "9:16", "4:5", "1:1"];

/* ---------- Laufzeit ---------- */

/** Sprechtempo Deutsch, ruhig gesprochen: etwa 2.3 Wörter pro Sekunde. */
export const WORDS_PER_SECOND = 2.3;
const spoken = (t: string) =>
  t
    .replace(/\[[^\]]*\]|\([^)]*\)/g, " ") // Regieanweisungen in Klammern zählen nicht
    .split(/\s+/)
    .filter((w) => /[\p{L}\p{N}]/u.test(w)).length;
export const estimateSeconds = (text: string) => {
  const w = spoken(text);
  return w ? Math.max(1, Math.round(w / WORDS_PER_SECOND)) : 0;
};
const num = (s: string) => Number(String(s).replace(",", "."));
/** Dauer: angegeben, sonst bei Statement und Sprecher aus dem Text geschätzt, bei Bildern 3 Sekunden */
export const beatSeconds = (b: Pick<ScriptBeat, "duration" | "audio">) => {
  const d = num(b.duration);
  if (d > 0) return d;
  const s = parseSound(b.audio);
  return s.kind === "bild" ? DEFAULT_SHOT_SECONDS : estimateSeconds(s.text) || DEFAULT_SHOT_SECONDS;
};
export const beatIsEstimated = (b: Pick<ScriptBeat, "duration">) => !(num(b.duration) > 0);
export const videoSeconds = (v: ScriptVideo) => v.beats.reduce((a, b) => a + beatSeconds(b), 0);
/** Startzeit jedes Moments (Sekunden) */
export const timecodes = (v: ScriptVideo) => {
  let t = 0;
  return v.beats.map((b) => {
    const at = t;
    t += beatSeconds(b);
    return at;
  });
};
export const fmtTc = (s: number) => `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}`;

/** «30 s», «1:30», «90», «2 min» → Sekunden */
export function parseLength(s: string): number {
  const t = String(s).trim().toLowerCase().replace(",", ".");
  if (!t) return 0;
  const mmss = t.match(/^(\d+):(\d{1,2})/);
  if (mmss) return Number(mmss[1]) * 60 + Number(mmss[2]);
  const n = Number.parseFloat(t);
  if (!(n > 0)) return 0;
  return /min|m\b/.test(t) && !/s/.test(t.replace("min", "")) ? Math.round(n * 60) : Math.round(n);
}
export const fmtSeconds = (s: number) => (s >= 60 ? `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")} min` : `${Math.round(s)} s`);

/* ---------- Zugriff ---------- */

export const beatsOf = (c: ScriptContent) => c.videos.flatMap((v) => v.beats);
export const questionsOf = (c: ScriptContent) => c.videos.flatMap((v) => v.questions);
export const fieldsOf = (c: ScriptContent) => [...c.brief, ...c.production];
/** Wert einer Angabe mit fester Bedeutung (erste, die nicht leer ist) */
export const fieldValue = (c: ScriptContent, key: string) => fieldsOf(c).find((f) => f.key === key && f.value.trim())?.value ?? "";
export const totalSeconds = (c: ScriptContent) => c.videos.reduce((a, v) => a + videoSeconds(v), 0);
/** Aufnahmen: jeder Moment ist eine Aufnahme */
export const shotCount = (c: ScriptContent) => beatsOf(c).length;
export const targetSeconds = (c: ScriptContent) => c.videos.reduce((a, v) => a + parseLength(v.targetLength), 0);
export const videoLabel = (c: ScriptContent, v: ScriptVideo) => v.title.trim() || (c.videos.length > 1 ? `Video ${c.videos.indexOf(v) + 1}` : "Video");

/** Drehplan: alle Aufnahmen aller Videos nach Ort, so wie am Drehtag gedreht wird. */
export function planByLocation(c: ScriptContent) {
  const multi = c.videos.length > 1;
  const groups = new Map<string, { video: string; videoNo: number; beat: ScriptBeat; tc: number }[]>();
  c.videos.forEach((v, vi) => {
    const tcs = timecodes(v);
    const places = placesOf(v);
    v.beats.forEach((b, bi) => {
      const key = places[bi] || "Ohne Ort";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push({ video: multi ? `V${vi + 1}` : "", videoNo: vi + 1, beat: b, tc: tcs[bi] ?? 0 });
    });
  });
  return groups;
}

/* ---------- Bausteine ---------- */

let seq = 0;
export const nid = () => `${Date.now().toString(36)}${(seq++).toString(36)}${Math.random().toString(36).slice(2, 6)}`;
export const newBeat = (p: Partial<ScriptBeat> = {}): ScriptBeat => ({ id: nid(), kind: "bild", label: "", who: "", visual: "", audio: "", shot: "", location: "", duration: "", overlay: "", note: "", ...p, options: [...(p.options ?? [])] });
export const newQuestion = (text = "", note = ""): ScriptQuestion => ({ id: nid(), text, note });
export const newField = (label = "", key = "", long = false, value = ""): ScriptField => ({ id: nid(), key, label, value, long });
export const newVideo = (p: Partial<ScriptVideo> = {}): ScriptVideo => ({ id: nid(), title: "", format: "16:9", targetLength: "", idea: "", beats: [], questions: [], ...p });

/** Angaben mit fester Bedeutung: Bezeichnung, mehrzeilig, Platzhalter. */
export const FIELD_KEYS: Record<string, { label: string; long: boolean; ph: string }> = {
  goal: { label: "Ziel", long: false, ph: "Was soll das Video bewirken?" },
  message: { label: "Botschaft", long: false, ph: "Der eine Gedanke, der hängen bleibt" },
  audience: { label: "Zielgruppe & Kanal", long: false, ph: "Wer schaut es, wo läuft es?" },
  tone: { label: "Stil & Musik", long: false, ph: "Stimmung, Tempo, Musik, Referenzen" },
  rules: { label: "Hinweise", long: true, ph: "Was beachten, was vermeiden – eine Zeile pro Punkt" },
  person: { label: "Interviewpartner", long: false, ph: "Name, Funktion, Unternehmen" },
  cta: { label: "Call-to-Action", long: false, ph: "Was soll man danach tun?" },
  shootDate: { label: "Datum", long: false, ph: "" },
  callTime: { label: "Zeit", long: false, ph: "z. B. 07:30 Treffpunkt Haupteingang" },
  locations: { label: "Ort", long: true, ph: "Adresse und Drehorte, eine Zeile pro Ort" },
  contact: { label: "Kontakt vor Ort", long: false, ph: "Name, Telefon" },
  cast: { label: "Personen", long: true, ph: "Wer vor der Kamera steht, eine Zeile pro Person" },
  equipment: { label: "Material", long: true, ph: "Kamera, Ton, Licht … eine Zeile pro Punkt" },
  notes: { label: "Offen / Notizen", long: true, ph: "Was noch zu klären ist" },
};
export const keyed = (key: string, value = "") => newField(FIELD_KEYS[key]?.label ?? key, key, FIELD_KEYS[key]?.long ?? false, value);
/** Feste Angaben: immer da, in dieser Reihenfolge. Eigene Angaben aus älteren Skripten folgen danach. */
export const BRIEF_KEYS = ["goal", "message", "tone", "rules"];
export const PRODUCTION_KEYS = ["shootDate", "callTime", "locations", "contact", "cast", "equipment", "notes"];
const LEGACY_BRIEF = ["audience", "person", "cta"];
/** Feste Angaben ergänzen und ordnen */
function fixFields(list: ScriptField[], keys: string[]) {
  const out = keys.map((k) => list.find((f) => f.key === k) ?? keyed(k));
  return [...out, ...list.filter((f) => !keys.includes(f.key) && (f.value.trim() || f.label.trim()))];
}

/* ---------- Vorlagen ---------- */

/** Ablauf pro Videoart: Struktur, keine Fülltexte. */
export function videoTemplate(type: ScriptType, p: Partial<ScriptVideo> = {}): ScriptVideo {
  const T = SCRIPT_TYPES[type];
  const v = newVideo({ format: T.format, targetLength: T.length, ...p });
  const b = (x: Partial<ScriptBeat>) => syncBeat(newBeat(x));
  if (type === "social")
    v.beats = [
      b({ visual: "", shot: "Nah", duration: "2", note: "Hook: die ersten Sekunden entscheiden" }),
      b({ audio: "Sprecher: " }),
      b({ shot: "Detail", audio: "[Musik]" }),
      b({ overlay: "Call-to-Action", audio: "[Musik]" }),
    ];
  if (type === "image")
    v.beats = [
      b({ shot: "Drohne", duration: "4", audio: "[Musik]" }),
      b({ audio: "Sprecher: " }),
      b({ shot: "Detail", audio: "[Musik]" }),
      b({ shot: "Halbnah", audio: "Mitarbeiter/in: " }),
      b({ visual: "Schlussbild", overlay: "Logo + Claim", audio: "[Musik]" }),
    ];
  if (type === "interview") {
    v.beats = [b({ shot: "Halbnah", audio: "Interviewpartner/in: " }), b({ shot: "Detail", audio: "[Musik]" }), b({ shot: "Halbnah", audio: "Interviewpartner/in: " }), b({ visual: "Schlussbild", overlay: "Logo", audio: "[Musik]" })];
    v.questions = [
      newQuestion("Stell dich bitte kurz vor: Name, Funktion, Unternehmen.", "Für die Einblendung"),
      newQuestion("Wie war die Situation vorher?"),
      newQuestion("Was hat sich verändert? Gibt es ein konkretes Beispiel?"),
      newQuestion("Was ist in einem Satz der grösste Mehrwert?", "Als Schlusssatz geeignet"),
    ];
  }
  if (type === "event")
    v.beats = [
      b({ shot: "Totale", audio: "[Musik]" }),
      b({ shot: "Detail", audio: "[Musik]" }),
      b({ shot: "Halbnah", audio: "Gast: ", overlay: "Name, Funktion" }),
      b({ shot: "Halbtotale", audio: "[Musik]" }),
      b({ visual: "Schlussbild", overlay: "Logo + Datum", audio: "[Musik]" }),
    ];
  return v;
}

export function scriptTemplate(type: ScriptType): ScriptContent {
  return { v: 4, brief: fixFields([], BRIEF_KEYS), production: fixFields([], PRODUCTION_KEYS), videos: [videoTemplate(type)] };
}

/* ---------- Einlesen (Browser-Eingaben, ältere Fassungen) ---------- */

const str = (v: unknown) => (typeof v === "string" ? v : v == null ? "" : String(v));
const obj = (v: unknown) => (v && typeof v === "object" ? (v as Record<string, unknown>) : {});
const arr = (v: unknown) => (Array.isArray(v) ? (v as unknown[]) : []);
const KINDS = Object.keys(BEAT_KINDS) as BeatKind[];

const readQuestion = (x: unknown): ScriptQuestion => {
  const q = obj(x);
  return { id: str(q.id) || nid(), text: str(q.text), note: str(q.note) };
};
/** Ältere Feldnamen auf die neuen abbilden */
const FIELD_RENAME: Record<string, string> = { crew: "cast", props: "equipment", mustShots: "rules", music: "tone", hook: "message" };
const readField = (x: unknown): ScriptField => {
  const f = obj(x);
  return { id: str(f.id) || nid(), key: str(f.key), label: str(f.label), value: str(f.value), long: !!f.long };
};
const readBeat = (x: unknown): ScriptBeat => {
  const b = obj(x);
  // frühere Texttafel: der Text gehört ins Bild (Einblendung), nicht in den Ton
  if (b.kind === "text") {
    const words = str(b.audio).trim();
    return newBeat({
      id: str(b.id) || nid(),
      kind: "bild",
      note: [str(b.label).trim(), str(b.note).trim()].filter(Boolean).join(": "),
      visual: str(b.visual) || "Texttafel",
      overlay: [str(b.overlay), words].filter((t) => t.trim()).join(" · "),
      audio: "",
      location: str(b.location),
      duration: str(b.duration),
    });
  }
  const kind = KINDS.includes(b.kind as BeatKind) ? (b.kind as BeatKind) : "bild";
  // Frühere Fassung: Art und Sprecher/in standen in eigenen Feldern, jetzt stehen sie im Ton
  let audio = str(b.audio);
  const who = str(b.who).trim();
  const prefix = kind === "statement" ? who || "Statement" : kind === "sprecher" ? "Sprecher" : "";
  if (prefix && parseSound(audio).kind === "bild") audio = `${prefix}: ${audio.trim()}`;
  const label = str(b.label).trim();
  const note = str(b.note).trim();
  return syncBeat(newBeat({
    id: str(b.id) || nid(),
    visual: str(b.visual),
    audio,
    shot: str(b.shot),
    location: str(b.location),
    duration: str(b.duration),
    overlay: str(b.overlay),
    note: label && !note.toLowerCase().includes(label.toLowerCase()) ? [label, note].filter(Boolean).join(": ") : note,
    options: arr(b.options).map(str).slice(0, 30),
  }));
};
const readVideo = (x: unknown, type: ScriptType): ScriptVideo => {
  const v = obj(x);
  return {
    id: str(v.id) || nid(),
    title: str(v.title),
    format: str(v.format) || SCRIPT_TYPES[type].format,
    targetLength: str(v.targetLength),
    idea: str(v.idea),
    beats: Array.isArray(v.beats) ? v.beats.map(readBeat) : arr(v.scenes).flatMap(fromScene).map(readBeat),
    questions: arr(v.questions).map(readQuestion),
  };
};

/** Szene der früheren Fassung → Momente. Die Art wird aus dem Inhalt erkannt. */
function fromScene(x: unknown): ScriptBeat[] {
  const s = obj(x);
  const rows = arr(s.rows).map(obj);
  return rows.map((r, i) => {
    const audio = str(r.audio);
    const options = arr(r.options).map(str);
    const words = audio.replace(/\[[^\]]*\]|\([^)]*\)/g, "").trim();
    const statement = options.length > 0 || /o-ton|statement|interview|stimme/i.test(`${audio} ${str(s.title)}`);
    const kind: BeatKind = statement ? "statement" : words.split(/\s+/).filter(Boolean).length >= 4 ? "sprecher" : "bild";
    const extra = obj(r.extra);
    const extras = Object.values(extra).map(str).filter((t) => t.trim());
    return newBeat({
      kind,
      label: i === 0 && !statement ? str(s.title) : "",
      who: statement ? str(s.title) : "",
      visual: [str(r.visual), ...extras].filter(Boolean).join("\n"),
      audio,
      shot: str(r.shot),
      location: str(s.location),
      duration: str(r.duration),
      overlay: str(r.overlay),
      options,
      note: i === 0 ? str(s.notes) : "",
    });
  });
}

/** Freie Abschnitte (frühere Fassung) → Briefing, Drehtag, ein Video */
function fromSections(r: Record<string, unknown>, type: ScriptType): ScriptContent {
  const brief: ScriptField[] = [];
  const production: ScriptField[] = [];
  const video = newVideo({ format: str(r.format) || SCRIPT_TYPES[type].format, targetLength: str(r.targetLength) });
  for (const x of arr(r.sections)) {
    const sec = obj(x);
    const title = str(sec.title);
    if (sec.kind === "fields") {
      const prodSec = /produktion|dreh/i.test(title);
      for (const f of arr(sec.fields).map(readField)) (isProduction(f) || (!f.key && prodSec) ? production : brief).push(f);
    } else if (sec.kind === "text") {
      if (str(sec.text).trim()) brief.push(newField(title || "Notizen", "", true, str(sec.text)));
    } else if (sec.kind === "questions") video.questions.push(...arr(sec.questions).map(readQuestion));
    else if (sec.kind === "scenes") video.beats.push(...arr(sec.scenes).flatMap(fromScene).map(readBeat));
  }
  return { v: 4, brief, production, videos: [video] };
}
const isProduction = (f: ScriptField) => PRODUCTION_KEYS.includes(f.key) || ["crew", "props"].includes(f.key);

/** Erste Fassung (fester Steckbrief) */
function fromV1(r: Record<string, unknown>, type: ScriptType): ScriptContent {
  const b = obj(r.brief);
  const prod = obj(r.production);
  const pick = (src: Record<string, unknown>, keys: string[]) => keys.map((k) => keyed(FIELD_RENAME[k] ?? k, str(src[k]))).filter((f) => f.value.trim());
  return {
    v: 4,
    brief: pick(b, ["goal", "audience", "message", "cta", "tone"]),
    production: pick(prod, ["shootDate", "callTime", "locations", "cast", "crew", "equipment", "notes"]),
    videos: [newVideo({ format: str(r.format) || SCRIPT_TYPES[type].format, targetLength: str(r.targetLength) || SCRIPT_TYPES[type].length, beats: arr(r.scenes).flatMap(fromScene).map(readBeat), questions: arr(r.questions).map(readQuestion) })],
  };
}

/** Fehlendes ergänzen, Unbekanntes verwerfen, ältere Fassungen umwandeln. */
export function normalizeContent(raw: unknown, type: ScriptType): ScriptContent {
  const c = readContent(raw, type);
  return { ...c, brief: fixFields(c.brief, BRIEF_KEYS), production: fixFields(c.production, PRODUCTION_KEYS) };
}
function readContent(raw: unknown, type: ScriptType): ScriptContent {
  const r = obj(raw);
  if (Array.isArray(r.videos)) {
    const videos = r.videos.map((v) => readVideo(v, type));
    return { v: 4, brief: arr(r.brief).map(readField), production: arr(r.production).map(readField), videos: videos.length ? videos : [videoTemplate(type)] };
  }
  if (Array.isArray(r.sections)) return fromSections(r, type);
  if (r.brief || r.scenes || r.production || r.questions) return fromV1(r, type);
  return { v: 4, brief: [], production: [], videos: [newVideo({ format: str(r.format) || SCRIPT_TYPES[type].format, targetLength: str(r.targetLength) })] };
}
